/** Parsed result of a valid gallery URL route. */
export interface GalleryRoute {
  /** The gallery short ID extracted from the path. */
  shortId: string
  /** `true` when the path ends with `/preview`; `false` for the normal view. */
  preview: boolean
}

/**
 * Parses a valid gallery route from the URL pathname.
 *
 * Accepted shapes:
 * - `/g/:shortId` → `{ shortId, preview: false }`
 * - `/g/:shortId/preview` → `{ shortId, preview: true }`
 * - `/view/:shortId` → `{ shortId, preview: false }`
 * - `/view/:shortId/preview` → `{ shortId, preview: true }`
 *
 * Any other shape (missing shortId, extra segments, wrong prefix) returns
 * `null`, which the caller treats as a not-found condition without a backend
 * request.
 *
 * @param pathname - The URL pathname string (e.g. `window.location.pathname`).
 * @returns A {@link GalleryRoute} when the path is valid, or `null` otherwise.
 */
export function parseGalleryRoute(pathname: string): GalleryRoute | null {
  const segments = pathname.split('/').filter(Boolean)
  const routePrefix = segments[0]
  const isSupportedPrefix = routePrefix === 'g' || routePrefix === 'view'
  if (!isSupportedPrefix || !segments[1]) {
    return null
  }
  if (segments.length === 2) {
    return { shortId: segments[1], preview: false }
  }
  if (segments.length === 3 && segments[2] === 'preview') {
    return { shortId: segments[1], preview: true }
  }
  return null
}
